import { useState, useEffect } from 'react'
import { AdminLayout } from '../components/layout/AdminLayout'
import { dashboardApi } from '@eshop/shared'

export function AdminDashboard() {
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await dashboardApi.getAdminStats()
      setStats(data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar estadísticas')
      console.error('Error loading admin stats:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando estadísticas...</div>
        </div>
      </AdminLayout>
    )
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadStats}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Reintentar
          </button>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Welcome Section - Nolavi Design System */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl p-8 shadow-md">
          <h1 className="text-3xl font-bold mb-2">¡Bienvenido al Panel de Administración!</h1>
          <p className="text-blue-100">
            Gestiona vendedores, productos, categorías y todo el sistema desde aquí.
          </p>
        </div>

        {/* Main Stats Grid - Nolavi Design System */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Usuarios"
            value={stats?.users?.total_users || 0}
            subtitle={`${stats?.users?.total_vendors || 0} vendedores`}
            icon="👥"
            color="blue"
            trend="+12%"
          />
          <StatCard
            title="Total Productos"
            value={stats?.products?.total || 0}
            subtitle={`${stats?.products?.active || 0} activos`}
            icon="📦"
            color="green"
            trend="+8%"
          />
          <StatCard
            title="Categorías"
            value={stats?.categories?.total || 0}
            subtitle="En el catálogo"
            icon="🏷️"
            color="neutral"
          />
          <StatCard
            title="Staff"
            value={stats?.users?.total_staff || 0}
            subtitle="Administradores"
            icon="👨‍💼"
            color="warning"
          />
        </div>

        {/* Detailed Stats - Nolavi Design System */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Users Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center">
              <span className="text-xl mr-2">👥</span>
              Desglose de Usuarios
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                <span className="text-neutral-700">Vendedores Activos</span>
                <span className="text-lg font-bold text-blue-500">
                  {stats?.users?.total_vendors || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-neutral-50 rounded-lg">
                <span className="text-neutral-700">Personal Staff</span>
                <span className="text-lg font-bold text-neutral-700">
                  {stats?.users?.total_staff || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-neutral-100 rounded-lg">
                <span className="text-neutral-700">Total Sistema</span>
                <span className="text-lg font-bold text-neutral-800">
                  {stats?.users?.total_users || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Products Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center">
              <span className="text-xl mr-2">📦</span>
              Estado de Productos
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-4 bg-success-50 rounded-lg">
                <span className="text-neutral-700">Productos Activos</span>
                <span className="text-lg font-bold text-success-600">
                  {stats?.products?.active || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-warning-50 rounded-lg">
                <span className="text-neutral-700">Borradores</span>
                <span className="text-lg font-bold text-warning-600">
                  {stats?.products?.draft || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-neutral-100 rounded-lg">
                <span className="text-neutral-700">Total en Catálogo</span>
                <span className="text-lg font-bold text-neutral-800">
                  {stats?.products?.total || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - Nolavi Design System */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4">
            Acciones Rápidas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <QuickActionCard
              icon="👥"
              title="Gestionar Vendedores"
              description="Ver y administrar todos los vendedores"
              link="/admin/vendors"
              color="blue"
            />
            <QuickActionCard
              icon="📦"
              title="Revisar Productos"
              description="Supervisar todos los productos del sistema"
              link="/admin/products"
              color="green"
            />
            <QuickActionCard
              icon="👨‍💼"
              title="Administrar Staff"
              description="Gestionar equipo administrativo"
              link="/admin/staff"
              color="neutral"
            />
          </div>
        </div>

        {/* System Health - Nolavi Design System */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4">
            Estado del Sistema
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="flex items-center space-x-4 p-4 bg-success-50 rounded-lg">
              <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
              <div>
                <p className="font-semibold text-neutral-800">Base de Datos</p>
                <p className="text-sm text-neutral-500">Operativo</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-4 bg-success-50 rounded-lg">
              <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
              <div>
                <p className="font-semibold text-neutral-800">API Backend</p>
                <p className="text-sm text-neutral-500">Operativo</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-4 bg-success-50 rounded-lg">
              <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
              <div>
                <p className="font-semibold text-neutral-800">Almacenamiento</p>
                <p className="text-sm text-neutral-500">Operativo</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

interface StatCardProps {
  title: string
  value: number
  subtitle: string
  icon: string
  color: 'blue' | 'green' | 'neutral' | 'warning'
  trend?: string
}

function StatCard({ title, value, subtitle, icon, color, trend }: StatCardProps) {
  // Nolavi Design System - Using subtle backgrounds instead of heavy gradients
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-success-500',
    neutral: 'bg-neutral-700',
    warning: 'bg-warning-500',
  }

  return (
    <div className={`${colorClasses[color]} text-white rounded-lg shadow-md p-6`}>
      <div className="flex items-start justify-between mb-4">
        <div className="text-3xl opacity-90">{icon}</div>
        {trend && (
          <span className="text-xs bg-white/20 px-2 py-1 rounded-md font-medium">
            {trend}
          </span>
        )}
      </div>
      <p className="text-3xl font-bold mb-1">{value}</p>
      <p className="text-sm font-semibold opacity-95">{title}</p>
      <p className="text-xs opacity-80 mt-1">{subtitle}</p>
    </div>
  )
}

interface QuickActionCardProps {
  icon: string
  title: string
  description: string
  link: string
  color: 'blue' | 'green' | 'neutral'
}

function QuickActionCard({ icon, title, description, link, color }: QuickActionCardProps) {
  // Nolavi Design System colors
  const colorClasses = {
    blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
    green: 'bg-success-50 hover:bg-success-50/80 border-success-500/20',
    neutral: 'bg-neutral-50 hover:bg-neutral-100 border-neutral-200',
  }

  return (
    <a
      href={link}
      className={`block p-5 border rounded-xl transition-all ${colorClasses[color]}`}
    >
      <div className="text-2xl mb-3">{icon}</div>
      <h4 className="font-semibold text-neutral-800 mb-1">{title}</h4>
      <p className="text-sm text-neutral-500">{description}</p>
    </a>
  )
}
